import { RefObject, useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"
import { IFinalEvent } from "types/event"
import { AnalyticsAccountType } from "services/analytics"
import { resolveEventRouteId } from "utils/eventPage"

export function useStickyBuyButton(
    contentRef: RefObject<HTMLDivElement | null>
) {
    const [showBuy, setShowBuy] = useState(false)
    const [isLockedAtFooter, setIsLockedAtFooter] = useState(false)
    const isLockedRef = useRef(false)
    const rafRef = useRef<number | null>(null)

    useEffect(() => {
        const handleScroll = () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
            }

            rafRef.current = requestAnimationFrame(() => {
                setShowBuy(window.scrollY > 200)

                if (!contentRef.current) {
                    return
                }

                const contentRect = contentRef.current.getBoundingClientRect()
                const buttonHeight = 100
                const viewportHeight = window.innerHeight
                const hysteresis = 50

                if (isLockedRef.current) {
                    if (
                        contentRect.bottom >
                        viewportHeight + buttonHeight + hysteresis
                    ) {
                        isLockedRef.current = false
                        setIsLockedAtFooter(false)
                    }
                    return
                }

                if (contentRect.bottom <= viewportHeight + buttonHeight) {
                    isLockedRef.current = true
                    setIsLockedAtFooter(true)
                }
            })
        }

        window.addEventListener("scroll", handleScroll, { passive: true })
        handleScroll()

        return () => {
            window.removeEventListener("scroll", handleScroll)
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current)
            }
        }
    }, [contentRef])

    return { showBuy, isLockedAtFooter }
}

export function useEventDetailsLifecycle({
    event,
    pageVisitSource,
    sendEventViewed,
    trackEvent,
}: {
    event?: IFinalEvent
    pageVisitSource?: string
    sendEventViewed: (eventId: string, eventTitle: string) => void
    trackEvent: (
        eventName: string,
        properties?: Record<string, unknown>,
        accountType?: AnalyticsAccountType,
        eventId?: string
    ) => void
}) {
    const router = useRouter()
    const eventViewedRef = useRef(false)

    useEffect(() => {
        if (!router.isReady || !event?.slug) return

        const currentParam = router.query.eventid as string | undefined
        if (!currentParam) return

        const resolvedCurrentParam = resolveEventRouteId(currentParam)

        if (
            currentParam === event.slug ||
            resolvedCurrentParam === event.slug
        ) {
            return
        }

        const [pathWithoutHash] = router.asPath.split("#")
        const [pathWithoutQuery, queryString] = pathWithoutHash.split("?")
        const segments = pathWithoutQuery.split("/")

        if (segments.length === 0) return

        segments[segments.length - 1] = event.slug
        const newPath = segments.join("/")
        const finalPath = queryString ? `${newPath}?${queryString}` : newPath

        router.replace(finalPath, undefined, { shallow: true })
    }, [event?.slug, router])

    useEffect(() => {
        if (event?._id && !eventViewedRef.current) {
            eventViewedRef.current = true
            sendEventViewed(event._id, event.en_title || event.ar_title || "")
        }
    }, [event?._id, event?.en_title, event?.ar_title, sendEventViewed])

    useEffect(() => {
        if (!pageVisitSource || !event?._id) return

        trackEvent(
            "source_referral",
            {
                category: "Referral",
                label: "source",
                value: pageVisitSource,
            },
            "event",
            event._id
        )
    }, [pageVisitSource, event?._id, trackEvent])
}
